<template>
  <div class="h-100">
    <v-chart ref="chart" :options="options" :echart.sync="echart" :ready="chartReady" @change:series="changeSeries" />
  </div>
</template>

<script>
import { format } from 'date-fns';
import { merge } from 'lodash';
import chartable from '@/mixins/chartable';
import motletable from '@/mixins/motletable';
import bus from '@/utils/bus';

export default {
  mixins: [chartable, motletable],
  props: {
    settings: {
      type: Object,
      required: true,
    },
    /*
     * Commented.by.KWShin.2024.03.07.
     * motlet에 넘어오는 데이터는 부모의 부모 컴포넌트에 선언된 motletOptions 이다.
     */
    motlet: {
      type: Object,
      required: true,
    },
    storeName: {
      type: String,
      required: true,
    },
    activeIncrement: Boolean,
  },
  data() {
    return {
      lastTimestamp: 0,
      drawCounter: 0,
      options: {
        xAxis: {
          type: 'category',
          axisLabel: {
            formatter: value => this.xAxisLabelFormatter(value),
            fontWeight: 'normal',
            fontSize: 9,
            color: '#bbbbaa',
          },
        },
        yAxis: {
          nameGap: 7,
          nameTextStyle: {
            fontWeight: 'normal',
            fontSize: 11,
            color: '#bbbbaa',
          },
          minInterval: 1,
          axisLabel: {
            inside: true,
            showMinLabel: false,
            formatter: value => this.yAxisLabelFormatter(value),
            fontWeight: 'normal',
            fontSize: 9,
            color: '#bbbbaa',
          },
        },
        series: [
          {
            type: 'line',
            markLine: {
              symbol: 'none',
              label: { show: false },
              lineStyle: {
                type: 'solid',
                color: '#b55a09',
                width: 1,
              },
              data: [{ xAxis: 0 }],
            },
          },
        ],
        grid: {
          top: '5px',
          left: '18px',
          right: '11px',
          bottom: '16px',
        },
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'line',
            z: 0,
            label: {
              show: true,
              formatter: params => format(parseInt(params.value), 'HH:mm:ss'),
            },
          },
          formatter: params => this.tooltipFormatter(params),
        },
        toolbox: {
          itemSize: 0,
          feature: {
            dataZoom: { yAxisIndex: 'none' },
          },
        },
      },
      echart: null,
    };
  },
  computed: {
    xAxisLabelFormatter() {
      return typeof this.settings.formatter.xAxisLabel == 'function'
        ? this.settings.formatter.xAxisLabel
        : function(value) {
            return format(parseInt(value), 'HH:mm:ss');
          };
    },
    yAxisLabelFormatter() {
      return typeof this.settings.formatter.yAxisLabel == 'function'
        ? this.settings.formatter.yAxisLabel
        : function(value) {
            return value;
          };
    },
    tooltipFormatter() {
      return typeof this.settings.formatter.tooltip == 'function' ? this.settings.formatter.tooltip : function() {};
    },
    eventCode() {
      return this.motlet.monitor.eventCode || this.motlet.monitor.monitorCode;
    },
    zooming() {
      const xAxis = this.echart?.getOption()?.xAxis ? this.echart.getOption().xAxis[0] : null;
      return xAxis?.rangeStart !== null && xAxis?.rangeEnd !== null;
    },
  },
  watch: {
    'settings.systemId': {
      handler(value, before) {
        if (before) this.drawMarkLine(parseInt(this.options.series[0].markLine.data[0].xAxis));
      },
    },
    /*
     * Removed.by.KWShin.2024.03.07.
     * 이 무지막지한 데이터를 deep 하게 watch 하다니...
     * 각설하고, X축 길이 설정값이 변경됐을 때 차트를 다시 그려야 하니
     * 필요할 때 부모 쪽에서 initializeChartOptions()를 호출해주는 것으로 변경.
     *
     * 헐~ 이걸 막으니까 차트가 안 그려지네....
     */
    settings: {
      deep: true,
      handler() {
        this.initializeChartOptions();
      },
    },
    zooming: {
      handler: 'changeZoomingHandler',
    },
    zoomRestore: {
      handler: 'changeZoomRestoreHandler',
    },
  },
  methods: {
    chartReady(echart, caller) {
      caller.addEventClickChart();
      echart.dispatchAction({
        type: 'takeGlobalCursor',
        key: 'dataZoomSelect',
        dataZoomSelectActive: true,
      });
    },
    changeZoomingHandler(active) {
      this.$store.dispatch('common/motlet/setZoomActive', { id: this.motletId, active });
    },
    changeZoomRestoreHandler() {
      if (this.zoomRestore) {
        this.echart.dispatchAction({ type: 'restore' });
        this.$store.dispatch('common/motlet/setZoomRestore', { id: this.motletId, restore: false });
      }
    },
    // add event chart click
    addEventClickChart() {
      const canvas = this.echart.getDom().getElementsByTagName('canvas')[0];
      canvas.addEventListener('click', event => {
        /*
         * echart에 'click' Event Listener를 등록하고
         * 또한 'dataZoomSelect' action도 dispatch를 하면
         * echart에서 drag시 click 이벤트가 발생하면서 이 컴포넌트에서 emit한 click:chart-${this.storeName} 이벤트를
         * 수신처리하는 쪽에서 변경된 시각에 맞추어 차트를 다시 그리기 때문에 zooming이 자동으로 해제되버리기 때문에
         * Motlet 쪽에서 처리하는 zooming 기능이 제대로 동작하지 않게 된다.
         * 따라서, echart에서 drag가 시작되면 zoomActive가 true로 설정되기 때문에
         * 여기서 zoomActive를 체크하여 drag가 된 (즉, dataZoomSelect가 발생한) 경우에는 click:chart-${this.storeName} 이벤트를 emit하지 않는다.
         * 이러면, drag가 발생하지 않고 click만 발생한 경우에도 정상적으로 동작하고
         * drag가 발생한 경우에도 정상적으로 동작한다.
         * Commendted.by.KWShin.2024.08.22.
         */
        if (this.zoomActive) return;
        const data = this.echart.getOption().xAxis[0].data;
        const pixels = data.map((value, index) => this.echart.convertToPixel({ xAxisIndex: 0 }, index));
        const closestValue = pixels.reduce(function(prev, curr) {
          return Math.abs(curr - event.offsetX) < Math.abs(prev - event.offsetX) ? curr : prev;
        });
        const index = pixels.findIndex(value => Math.abs(value) === closestValue);
        bus.$emit(`click:chart-${this.storeName}`, data[index]);
        bus.$emit(`set:active-increment-${this.storeName}`, false);
      });
    },
    // draw mark-line
    drawMarkLine(timestamp) {
      const data = this.echart.getOption().xAxis[0].data;
      const closest = data.reduce(function(prev, curr) {
        return Math.abs(curr - timestamp) < Math.abs(prev - timestamp) ? curr : prev;
      });
      this.options.series[0].markLine.data[0].xAxis = closest.toString();
      bus.$emit(`change:timestamp-${this.storeName}`, { eventCode: this.eventCode, timestamp: closest });
    },
    initializeChartOptions() {
      this.options.xAxis.data = [];
      this.options.series.forEach(s => (s.data = []));
      this.options = merge({}, this.options, this.settings.chart);
      /*
       * 정말이지 환장하겠군...
       * Expansion panel 내에 위치한 Tab 내에 차트가 위치하는데,
       * panel이 오므려졌다가 펼쳐지거나 tab이 전환되었을 때 차트를 다시그려주어야 차트가 나타나는데,
       * 차트를 다시 그려주면 dataZoomSelect 액션을 전달하는 기능이 사라져 버린다.
       * 그래서, 여기서 차트를 초기화할 때마다 다시 dispatchAction()을 등록!
       * Commented.by.KWShin.2024.08.22.
       */
      this.$nextTick(() => {
        this.echart.dispatchAction({
          type: 'takeGlobalCursor',
          key: 'dataZoomSelect',
          dataZoomSelectActive: true,
        });
      });
    },
    getMarkLineXAxis() {
      let markLineXAxis = 0;
      try {
        const {
          series: [
            {
              markLine: {
                data: [{ xAxis }],
              },
            },
          ],
        } = this.echart.getOption();
        markLineXAxis = xAxis;
      } catch (e) {
        markLineXAxis = 0;
      }
      return parseInt(markLineXAxis);
    },
    changeSeries(series) {
      const [{ data }] = series;
      if (!data.length) return;

      if (this.activeIncrement) {
        const timestamp = data[data.length - 1][2];
        if (timestamp != this.lastTimestamp) {
          this.lastTimestamp = timestamp;
          this.drawMarkLine(timestamp);
        }
      } else {
        this.drawMarkLine(this.settings.datetime.getTime());
      }
    },
  },
  created() {
    bus.$on(`click:chart-${this.storeName}`, this.drawMarkLine);
  },
  beforeDestroy() {
    bus.$off(`click:chart-${this.storeName}`);
  },
};
</script>
